import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { FeatureFlagsProvider } from '@/contexts/FeatureFlagsContext';
import { FeatureGate } from '@/components/FeatureGate';
import NotFoundPage from '@/pages/NotFoundPage';

// Existing HR App Pages
import LoginPage from '@/pages/LoginPage';
import TenantLoginPage from '@/pages/TenantLoginPage';
import DashboardLayout from '@/components/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import Companies from '@/pages/Companies';
import Teams from '@/pages/Teams';
import ClientAnalytics from '@/pages/ClientAnalytics';
import TargetManagement from '@/pages/TargetManagement';
import Positions from '@/pages/Positions';
import Resumes from '@/pages/Resumes';
import Interviews from '@/pages/Interviews';
import Closures from '@/pages/Closures';
import Joinings from '@/pages/Joinings';
import Recruiters from '@/pages/Recruiters';
import UserManagement from '@/pages/UserManagement';
import Announcements from '@/pages/Announcements';
import OpenPositionsSummary from '@/pages/OpenPositionsSummary';
import PositionTracker from '@/pages/PositionTracker';
import OrgSettings from '@/pages/OrgSettings';
import TenantSupport from '@/pages/TenantSupport';
import SuperAdminSupport from '@/pages/SuperAdminSupport';
import CRM from '@/pages/CRM';
import SuperAdminCRM from '@/pages/SuperAdminCRM';
import LaunchCleanup from '@/pages/LaunchCleanup';
import SuperAdminVersion from '@/pages/SuperAdminVersion';
import Instructions from '@/pages/Instructions';
import BulkUpload from '@/pages/BulkUpload';
import CCR from '@/pages/CCR';
import CCRSummary from '@/pages/CCRSummary';
import KnowledgeHub from '@/pages/KnowledgeHub';
import Attendance from '@/pages/Attendance';
import LeaveManagement from '@/pages/LeaveManagement';
import EmployeeDirectory from '@/pages/EmployeeDirectory';
import PerformanceManagement from '@/pages/PerformanceManagement';
import OnboardingForm from '@/pages/OnboardingForm';
import EmployeePolicies from '@/pages/EmployeePolicies';
import TalentVault from '@/pages/TalentVault';
import ActivityLog from '@/pages/ActivityLog';
import RevenueSummary from '@/pages/RevenueSummary';

// New Pages from base code
import JobPostings from '@/pages/JobPostings';
import CareersPage from '@/pages/CareersPage';
import JobPostingPage from '@/pages/JobPostingPage';
import NextMove from '@/pages/NextMove';
import RecruiterIQ from '@/pages/RecruiterIQ';
import AIUsageAnalytics from '@/pages/AIUsageAnalytics';
import LMS from '@/pages/LMS';
import Polls from '@/pages/Polls';
import Resignations from '@/pages/Resignations';
import Clearance from '@/pages/Clearance';
import FnF from '@/pages/FnF';
import ExitSurveys from '@/pages/ExitSurveys';
import ExitReports from '@/pages/ExitReports';
import MyProfile from '@/pages/MyProfile';
import TalentBuzz from '@/pages/TalentBuzz';

// Super Admin Pages
import SuperAdminLogin from '@/pages/SuperAdminLogin';
import SuperAdminLayout from '@/pages/SuperAdminLayout';
import SuperAdminEmailSettings from '@/pages/SuperAdminEmailSettings';
import SuperAdminEmailSetupGuide from '@/pages/SuperAdminEmailSetupGuide';
import SuperAdminTenantSafety from '@/pages/SuperAdminTenantSafety';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SuperAdminTenants from '@/pages/SuperAdminTenants';
import SuperAdminThemes from '@/pages/SuperAdminThemes';
import SuperAdminDomains from '@/pages/SuperAdminDomains';
import SuperAdminPayments from '@/pages/SuperAdminPayments';
import SuperAdminReports from '@/pages/SuperAdminReports';
import SuperAdminDemoRequests from '@/pages/SuperAdminDemoRequests';
import SuperAdminDemoPortals from '@/pages/SuperAdminDemoPortals';
import SuperAdminVersions from '@/pages/SuperAdminVersions';
import SuperAdminSettings from '@/pages/SuperAdminSettings';
import SuperAdminBilling from '@/pages/SuperAdminBilling';
import SuperAdminHealth from '@/pages/SuperAdminHealth';
import SuperAdminDbHealth from '@/pages/SuperAdminDbHealth';
import SuperAdminClientErrors from '@/pages/SuperAdminClientErrors';
import SuperAdminUsers from '@/pages/SuperAdminUsers';
import SuperAdminSubscriptions from '@/pages/SuperAdminSubscriptions';
import SuperAdminImpersonation from '@/pages/SuperAdminImpersonation';

import SuperAdminModuleControl from '@/pages/SuperAdminModuleControl';
import SuperAdminModuleHub from '@/pages/SuperAdminModuleHub';
import SuperAdminCMS from '@/pages/SuperAdminCMS';

// Tenant Settings Pages
import TenantSubscription from '@/pages/TenantSubscription';
import TenantBranding from '@/pages/TenantBranding';
import TenantUsageDashboard from '@/pages/TenantUsageDashboard';
import TenantGDPRSettings from '@/pages/TenantGDPRSettings';
import TenantCustomCSS from '@/pages/TenantCustomCSS';
import RoleManagement from '@/pages/RoleManagement';
import Integrations from '@/pages/Integrations';
const DataMigrationPage = lazy(() => import('@/pages/DataMigrationPage'));
const IncentivesPage = lazy(() => import('@/pages/Incentives'));

// Super Admin Extended Pages
import PlanManagement from '@/pages/PlanManagement';
import TenantDetail from '@/pages/TenantDetail';
import SuperAdminActivityLogs from '@/pages/SuperAdminActivityLogs';

// Landing & SaaS-specific
import LandingPage from '@/pages/LandingPage';
// Below: lazy-loaded so they don't bloat the main bundle (SEO Core Web Vitals win).
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const TermsAndConditions = lazy(() => import('@/pages/TermsAndConditions'));
const AboutUs = lazy(() => import('@/pages/AboutUs'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const PublicHelpCentre = lazy(() => import('@/pages/PublicHelpCentre'));
const DevelopersPage = lazy(() => import('@/pages/DevelopersPage'));
const TenantHelpCentre = lazy(() => import('@/pages/TenantHelpCentre'));

// Feature Pages — 5 dedicated static ones (SEO-friendly, no API fetch) + CMS-driven fallback
const FeatureRecruiterIQ = lazy(() => import('@/pages/FeatureRecruiterIQ'));
const FeatureHireTrack = lazy(() => import('@/pages/FeatureHireTrack'));
const FeatureHRMS = lazy(() => import('@/pages/FeatureHRMS'));
const FeatureNextMove = lazy(() => import('@/pages/FeatureNextMove'));
const FeatureTalentVault = lazy(() => import('@/pages/FeatureTalentVault'));
const FeaturePage = lazy(() => import('@/pages/FeaturePage'));

// Blog
const BlogList = lazy(() => import('@/pages/BlogList'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const SuperAdminBlog = lazy(() => import('@/pages/SuperAdminBlog'));

// Components
import CookieConsentBanner from '@/components/CookieConsentBanner';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { I18nProvider } from '@/contexts/I18nContext';
import { CurrencyProvider } from '@/contexts/CurrencyContext';

import '@/App.css';

const SmartRoot = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <LandingPage />;
};

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
};

const RoleRoute = ({ children, roles, excludeRoles = [] }) => {
  const userData = localStorage.getItem('user');
  if (!userData) return <Navigate to="/" replace />;
  const user = JSON.parse(userData);
  const effectiveRole = ['intern', 'it', 'finance', 'marketing'].includes(user.role) ? 'recruiter' : user.role;
  if (excludeRoles.includes(effectiveRole)) return <Navigate to="/" replace />;
  const hasAccess = roles.includes(effectiveRole) || (effectiveRole === 'admin_support' && roles.includes('admin'));
  return hasAccess ? children : <Navigate to="/" replace />;
};

const SuperAdminRoute = ({ children }) => {
  const token = localStorage.getItem('superAdminToken');
  return token ? children : <Navigate to="/super-admin/login" replace />;
};

const AuthCallback = () => {
  const [processed, setProcessed] = React.useState(false);
  React.useEffect(() => {
    if (processed) return;
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      if (sessionId) {
        const processAuth = async () => {
          try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const tenant = localStorage.getItem('pendingTenantSlug');
            const response = await fetch(`${BACKEND_URL}/api/auth/google/callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId, tenant_slug: tenant })
            });
            if (!response.ok) throw new Error('Auth failed');
            const data = await response.json();
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            if (data.tenant) localStorage.setItem('tenant', JSON.stringify(data.tenant));
            localStorage.removeItem('pendingTenantSlug');
            window.location.href = '/';
          } catch { window.location.href = '/login'; }
        };
        processAuth();
        setProcessed(true);
      }
    } else { window.location.href = '/login'; }
  }, [processed]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
      <CurrencyProvider>
      <BrowserRouter>
        <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',color:'#64748b',fontSize:14}}>Loading…</div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<TenantLoginPage />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:slug" element={<CareersPage />} />
          <Route path="/careers/:slug/:jobSlug" element={<JobPostingPage />} />
          {/* Known feature pages — static, no API spinner (fixes Google Soft 404) */}
          <Route path="/features/recruiter-iq" element={<FeatureRecruiterIQ />} />
          <Route path="/features/hiretrack" element={<FeatureHireTrack />} />
          <Route path="/features/hrms" element={<FeatureHRMS />} />
          <Route path="/features/nextmove" element={<FeatureNextMove />} />
          <Route path="/features/talentvault" element={<FeatureTalentVault />} />
          {/* Any other slug falls through to the CMS-driven page */}
          <Route path="/features/:slug" element={<FeaturePage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/help" element={<PublicHelpCentre />} />
          <Route path="/developers" element={<DevelopersPage />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="tenants" element={<SuperAdminTenants />} />
            <Route path="themes" element={<SuperAdminThemes />} />
            <Route path="domains" element={<SuperAdminDomains />} />
            <Route path="payments" element={<SuperAdminPayments />} />
            <Route path="analytics" element={<SuperAdminReports />} />
            <Route path="reports" element={<SuperAdminReports />} />
            <Route path="plans" element={<PlanManagement />} />
            <Route path="tenants/:slug" element={<TenantDetail />} />
            <Route path="tenants/:slug/modules" element={<SuperAdminModuleControl />} />
            <Route path="module-control" element={<SuperAdminModuleHub />} />
            <Route path="activity-logs" element={<SuperAdminActivityLogs />} />
            <Route path="demo-requests" element={<SuperAdminDemoRequests />} />
            <Route path="demo-portals" element={<SuperAdminDemoPortals />} />
            <Route path="versions" element={<SuperAdminVersions />} />
            <Route path="settings" element={<SuperAdminSettings />} />
            <Route path="cms" element={<SuperAdminCMS />} />
            <Route path="blog" element={<SuperAdminBlog />} />
            <Route path="billing" element={<SuperAdminBilling />} />
            <Route path="health" element={<SuperAdminHealth />} />
            <Route path="db-health" element={<SuperAdminDbHealth />} />
            <Route path="client-errors" element={<SuperAdminClientErrors />} />
            <Route path="admins" element={<SuperAdminUsers />} />
            <Route path="support" element={<SuperAdminSupport />} />
            <Route path="crm" element={<SuperAdminCRM />} />
            <Route path="launch-cleanup" element={<LaunchCleanup />} />
            <Route path="version" element={<SuperAdminVersion />} />
            <Route path="email" element={<SuperAdminEmailSettings />} />
            <Route path="email/setup-guide" element={<SuperAdminEmailSetupGuide />} />
            <Route path="tenant-safety" element={<SuperAdminTenantSafety />} />
            <Route path="subscriptions" element={<SuperAdminSubscriptions />} />
            <Route path="impersonation" element={<SuperAdminImpersonation />} />
          </Route>
          
          {/* Tenant HR App Routes */}
          <Route path="/" element={<SmartRoot><FeatureFlagsProvider><DashboardLayout /></FeatureFlagsProvider></SmartRoot>}>
            <Route index element={<Dashboard />} />
            <Route path="companies" element={<RoleRoute roles={['admin']}><Companies /></RoleRoute>} />
            <Route path="teams" element={<RoleRoute roles={['admin']}><Teams /></RoleRoute>} />
            <Route path="analytics" element={<RoleRoute roles={['admin', 'team_leader']}><ClientAnalytics /></RoleRoute>} />
            <Route path="targets" element={<TargetManagement />} />
            <Route path="positions" element={<Positions />} />
            <Route path="resumes" element={<Resumes />} />
            <Route path="interviews" element={<Interviews />} />
            <Route path="closures" element={<Closures />} />
            <Route path="joinings" element={<Joinings />} />
            <Route path="recruiters" element={<RoleRoute roles={['admin', 'team_leader']}><Recruiters /></RoleRoute>} />
            <Route path="user-management" element={<RoleRoute roles={['admin']}><UserManagement /></RoleRoute>} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="open-positions" element={<RoleRoute roles={['admin', 'team_leader']}><OpenPositionsSummary /></RoleRoute>} />
            <Route path="position-tracker" element={<RoleRoute roles={['admin', 'team_leader']}><PositionTracker /></RoleRoute>} />
            <Route path="instructions" element={<Instructions />} />
            <Route path="help-centre" element={<TenantHelpCentre />} />
            <Route path="bulk-upload" element={<RoleRoute roles={['admin', 'team_leader']}><FeatureGate flagKey="bulk_upload" featureName="Bulk Upload"><BulkUpload /></FeatureGate></RoleRoute>} />
            <Route path="ccr" element={<CCR />} />
            <Route path="ccr-summary" element={<CCRSummary />} />
            <Route path="knowledge-hub" element={<KnowledgeHub />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="leave-management" element={<LeaveManagement />} />
            <Route path="employee-directory" element={<RoleRoute roles={['admin', 'team_leader']}><EmployeeDirectory /></RoleRoute>} />
            <Route path="performance" element={<FeatureGate flagKey="advanced_hrms" featureName="Advanced HRMS"><PerformanceManagement /></FeatureGate>} />
            <Route path="onboarding-form" element={<OnboardingForm />} />
            <Route path="policies" element={<EmployeePolicies />} />
            <Route path="talent-vault" element={<TalentVault />} />
            <Route path="activity-log" element={<RoleRoute roles={['admin']} excludeRoles={['admin_support']}><ActivityLog /></RoleRoute>} />
            <Route path="revenue" element={<RoleRoute roles={['admin']} excludeRoles={['admin_support']}><RevenueSummary /></RoleRoute>} />
            <Route path="job-postings" element={<RoleRoute roles={['admin', 'team_leader', 'recruiter']}><JobPostings /></RoleRoute>} />
            <Route path="nextmove" element={<RoleRoute roles={['admin']}><FeatureGate flagKey="nextmove_analytics" featureName="NextMove Analytics"><NextMove /></FeatureGate></RoleRoute>} />
            <Route path="recruiter-iq" element={<FeatureGate flagKey="recruiteriq_v2" featureName="RecruiterIQ AI Suite"><RecruiterIQ /></FeatureGate>} />
            <Route path="ai-analytics" element={<RoleRoute roles={['admin']}><AIUsageAnalytics /></RoleRoute>} />
            <Route path="lms" element={<LMS />} />
            <Route path="polls" element={<Polls />} />
            <Route path="resignations" element={<RoleRoute roles={['admin', 'team_leader', 'recruiter']}><Resignations /></RoleRoute>} />
            <Route path="clearance" element={<RoleRoute roles={['admin', 'team_leader', 'recruiter']}><Clearance /></RoleRoute>} />
            <Route path="fnf" element={<RoleRoute roles={['admin']} excludeRoles={['admin_support']}><FnF /></RoleRoute>} />
            <Route path="exit-surveys" element={<RoleRoute roles={['admin', 'team_leader', 'recruiter']}><ExitSurveys /></RoleRoute>} />
            <Route path="exit-reports" element={<RoleRoute roles={['admin', 'team_leader']}><ExitReports /></RoleRoute>} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="profile/:userId" element={<MyProfile />} />
            <Route path="talent-buzz" element={<TalentBuzz />} />
            {/* SaaS Tenant Settings */}
            <Route path="subscription" element={<RoleRoute roles={['admin']}><TenantSubscription /></RoleRoute>} />
            <Route path="branding" element={<RoleRoute roles={['admin']}><FeatureGate flagKey="custom_branding" featureName="Custom Branding"><TenantBranding /></FeatureGate></RoleRoute>} />
            <Route path="usage" element={<RoleRoute roles={['admin']}><TenantUsageDashboard /></RoleRoute>} />
            <Route path="privacy-settings" element={<RoleRoute roles={['admin']}><TenantGDPRSettings /></RoleRoute>} />
            <Route path="custom-css" element={<RoleRoute roles={['admin']}><TenantCustomCSS /></RoleRoute>} />
            <Route path="roles" element={<RoleRoute roles={['admin']}><RoleManagement /></RoleRoute>} />
            <Route path="org-settings" element={<RoleRoute roles={['admin']}><OrgSettings /></RoleRoute>} />
            <Route path="support" element={<RoleRoute roles={['admin', 'admin_support']}><TenantSupport /></RoleRoute>} />
            <Route path="crm" element={<RoleRoute roles={['admin']}><CRM /></RoleRoute>} />
            <Route path="invoices" element={<RoleRoute roles={['admin']}><InvoicesPage /></RoleRoute>} />
            <Route path="integrations" element={<RoleRoute roles={['admin']}><Integrations /></RoleRoute>} />
            <Route path="data-migration" element={<RoleRoute roles={['admin']}><DataMigrationPage /></RoleRoute>} />
            <Route path="incentives" element={<IncentivesPage />} />
          </Route>
          
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <ImpersonationBanner />
      </BrowserRouter>
      </CurrencyProvider>
      </I18nProvider>
      <Toaster position="top-right" />
      <CookieConsentBanner />
    </div>
  );
}

export default App;
